import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowRight, Coffee, ChevronDown } from "lucide-react";
import ScrollIndicator from "../components/ScrollIndicator";

gsap.registerPlugin(ScrollTrigger);

export default function Hero() {
  const rootRef = useRef(null);
  const canvasRef = useRef(null);
  const chaptersRef = useRef([]);
  const buttonsRef = useRef(null);
  const scrollHintRef = useRef(null);
  const [indicatorOpacity, setIndicatorOpacity] = useState(1);

  const frameCount = 76;
  const currentFrame = (index) =>
    `/hero-frames/ezgif-frame-${(index + 1).toString().padStart(3, "0")}.jpg`;

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    const chapters = chaptersRef.current;
    const buttons = buttonsRef.current;

    const setCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    setCanvasSize();

    // ─── IMAGE PRELOAD ────────────────────────────────────────────
    const images = [];
    let loadedCount = 0;

    for (let i = 0; i < frameCount; i++) {
      const img = new Image();
      img.src = currentFrame(i);
      img.onload = () => {
        loadedCount++;
        if (loadedCount === 1) render(0); // Draw first frame as soon as possible
      };
      images.push(img);
    }

    function render(frameIndex) {
      const idx = Math.min(Math.max(Math.round(frameIndex || 0), 0), frameCount - 1);
      const img = images[idx];
      if (!img || !img.complete) return;

      const canvasRatio = canvas.width / canvas.height;
      const imgRatio = img.width / img.height;

      let rw, rh, x, y;
      if (canvasRatio > imgRatio) {
        rw = canvas.width;
        rh = canvas.width / imgRatio;
        x = 0;
        y = (canvas.height - rh) / 2;
      } else {
        rh = canvas.height;
        rw = canvas.height * imgRatio;
        y = 0;
        x = (canvas.width - rw) / 2;
      }

      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(img, x, y, rw, rh);

      // Dark overlay
      context.fillStyle = "rgba(26,15,7,0.4)";
      context.fillRect(0, 0, canvas.width, canvas.height);
    }

    const renderOnResize = () => {
      setCanvasSize();
      render(currentFrameIdx);
    };
    let currentFrameIdx = 0;
    window.addEventListener("resize", renderOnResize);

    // ─── INITIAL STATES ───────────────────────────────────────────
    gsap.set(chapters.slice(1), { autoAlpha: 0, y: 60 });
    gsap.set(chapters[0], { autoAlpha: 1, y: 0 });
    gsap.set(buttons, { autoAlpha: 0, y: 40 });
    gsap.set(scrollHintRef.current, { autoAlpha: 1 });

    // ─── SINGLE UNIFIED TIMELINE ──────────────────────────────────
    // Using a proxy object for the frame index so we can tween it
    const frameProxy = { value: 0 };

    const masterTL = gsap.timeline({
      scrollTrigger: {
        trigger: rootRef.current,
        start: "top top",
        end: "+=500%",
        scrub: 0.6,
        pin: true,
      },
    });

    // The total timeline duration will be ~12 units. We distribute frames across it.

    // ── Ch 0: visible from 0→2, fades out 2→3 ─────────────────
    masterTL
      .to(frameProxy, {
        value: 15,
        duration: 2,
        ease: "none",
        onUpdate() {
          currentFrameIdx = Math.round(frameProxy.value);
          render(currentFrameIdx);
        },
      })
      .to(chapters[0], { autoAlpha: 0, y: -40, duration: 1 }, 1.5);

    // ── Ch 1: in 2.5→3.5, out 4.5→5.5 ────────────────────────
    masterTL
      .to(chapters[1], { autoAlpha: 1, y: 0, duration: 1 }, 2.5)
      .to(frameProxy, {
        value: 30,
        duration: 3,
        ease: "none",
        onUpdate() {
          currentFrameIdx = Math.round(frameProxy.value);
          render(currentFrameIdx);
        },
      }, 2)
      .to(chapters[1], { autoAlpha: 0, y: -40, duration: 1 }, 4.5);

    // ── Ch 2: in 5→6, out 7→8 ────────────────────────────────
    masterTL
      .to(chapters[2], { autoAlpha: 1, y: 0, duration: 1 }, 5)
      .to(frameProxy, {
        value: 45,
        duration: 3,
        ease: "none",
        onUpdate() {
          currentFrameIdx = Math.round(frameProxy.value);
          render(currentFrameIdx);
        },
      }, 5)
      .to(chapters[2], { autoAlpha: 0, y: -40, duration: 1 }, 7);

    // ── Ch 3: in 7.5→8.5, out 9.5→10.5 ──────────────────────
    masterTL
      .to(chapters[3], { autoAlpha: 1, y: 0, duration: 1 }, 7.5)
      .to(frameProxy, {
        value: 60,
        duration: 3,
        ease: "none",
        onUpdate() {
          currentFrameIdx = Math.round(frameProxy.value);
          render(currentFrameIdx);
        },
      }, 7.5)
      .to(chapters[3], { autoAlpha: 0, y: -40, duration: 1 }, 9.5)
      // Fade out scroll hint before chapter 4 buttons appear
      .to(scrollHintRef.current, { autoAlpha: 0, duration: 0.5 }, 9);

    // ── Ch 4: in 10→11, buttons in 11→11.5, hold 11.5→13 ────
    masterTL
      .to(chapters[4], { autoAlpha: 1, y: 0, duration: 1 }, 10)
      .to(frameProxy, {
        value: 75,
        duration: 3,
        ease: "none",
        onUpdate() {
          currentFrameIdx = Math.round(frameProxy.value);
          render(currentFrameIdx);
        },
      }, 10)
      .to(buttons, { autoAlpha: 1, y: 0, duration: 0.8 }, 11)
      .to({}, { duration: 2 }); // hold

    return () => {
      masterTL.scrollTrigger?.kill();
      masterTL.kill();
      ScrollTrigger.getAll().forEach((st) => st.kill());
      window.removeEventListener("resize", renderOnResize);
    };
  }, []);

  const chapters = [
    { leftText: "O café que muda", rightText: "o que você sabe sobre café." },
    { leftText: "+ de 50 tipos de", rightText: "cafés especiais" },
    { leftText: "O ponto exato", rightText: "da perfeição." },
    { leftText: "O tesouro", rightText: "de Minas." },
    { leftText: "No centro", rightText: "de Alfenas." },
  ];

  return (
    <section
      id="top"
      ref={rootRef}
      className="relative w-full h-[100dvh] bg-espresso text-cream overflow-hidden"
    >
      <canvas
        ref={canvasRef}
        className="absolute top-0 left-0 w-full h-full z-0 pointer-events-none"
      />
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-espresso/80 via-transparent to-espresso/80 pointer-events-none" />

      <ScrollIndicator 
        opacity={indicatorOpacity} 
        top="100px" 
      />

      {/* Chapters */}
      <div className="relative z-10 w-full h-full">
        {chapters.map((chapter, index) => (
          <div
            key={index}
            ref={(el) => (chaptersRef.current[index] = el)}
            className="absolute top-1/2 -translate-y-1/2 w-full left-0 px-6 md:px-10"
            style={{ pointerEvents: "none" }}
          >
            <div className="max-w-[1400px] mx-auto w-full flex flex-col md:flex-row md:justify-between items-center gap-4 md:gap-10">
              <h1 className="font-display font-bold text-center md:text-left text-5xl sm:text-6xl md:text-7xl lg:text-[90px] leading-[1.05] tracking-tight text-cream w-full md:w-1/2 drop-shadow-lg">
                {chapter.leftText}
              </h1>
              <h1 className="font-display font-bold text-center md:text-right text-5xl sm:text-6xl md:text-7xl lg:text-[90px] leading-[1.05] tracking-tight text-gold w-full md:w-1/2 drop-shadow-lg">
                {chapter.rightText}
              </h1>
            </div>
          </div>
        ))}

        {/* Scroll hint — pulsing arrow */}
        <div
          ref={scrollHintRef}
          className="absolute bottom-[4%] md:bottom-[5%] left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-1 pointer-events-none"
        >
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-cream/50">
            Role a página
          </span>
          <ChevronDown
            size={24}
            className="text-gold animate-bounce"
          />
        </div>

        {/* Buttons — independent element, animated by ref */}
        <div
          ref={buttonsRef}
          className="absolute bottom-[6%] md:bottom-[14%] left-0 w-full px-6 md:px-10 z-20"
          style={{ pointerEvents: "auto" }}
        >
          <div className="max-w-[1400px] mx-auto flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="#sobre" className="btn-gold pulse-ring">
              Conheça o Empório <ArrowRight size={16} />
            </a>
            <a href="#produtos" className="btn-ghost">
              <Coffee size={16} /> Ver produtos
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
